// Shared
import Configs from '@/Configs';
import { createQueryKeysInvalidate } from '@/queries/createQueryKeysInvalidate';
import type { QueryKey } from '@/types/queries';

export const queriesKeys = {
  getAdoptionCalendar: (companyID: QueryKey, employeeID: QueryKey, year: number, month: number) => [
    Configs.platformGetAdoptionCalendarAPI,
    companyID,
    employeeID,
    year,
    month,
  ],

  getAdoptionConfig: (companyID: QueryKey, employeeID: QueryKey, moduleSlug: string) => [
    Configs.platformGetAdoptionConfigAPI,
    companyID,
    employeeID,
    moduleSlug,
  ],

  getPunchEmployeeProjects: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetPuchInOutCompanyProjectPerEmployeeAPI,
    companyID,
    employeeID,
  ],

  getEmployeeActiveContract: (companyID: QueryKey, employeeID: QueryKey, weekStartDate: number) => [
    Configs.platformGetEmployeeContractAPI,
    companyID,
    employeeID,
    weekStartDate,
  ],

  /** Contracts for a single employee (`platformGetEmployeeContractsAPI`). */
  getEmployeeContracts: (companyID: QueryKey, employeeID: QueryKey, targetEmployeeID: QueryKey) => [
    Configs.platformGetEmployeeContractsAPI,
    companyID,
    employeeID,
    targetEmployeeID,
  ],

  /**
   * Prefix for all per-employee contract queries for this actor. TanStack matches every key that
   * starts with this triple (all `getEmployeeContracts(companyID, employeeID, *)` observers).
   */
  allEmployeeContractsForActor: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetEmployeeContractsAPI,
    companyID,
    employeeID,
  ],

  /** All employee contracts for the company (`platformGetCompanyContractsAPI`). */
  getCompanyContracts: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetCompanyContractsAPI,
    companyID,
    employeeID,
  ],

  /**
   * One page of company contracts. Starts with `getCompanyContracts` so a prefix
   * invalidation refreshes every page.
   */
  getCompanyContractsPage: (
    companyID: QueryKey,
    employeeID: QueryKey,
    limit: QueryKey,
    cursor: QueryKey,
    employeeIDs: QueryKey,
    agreementIDs: QueryKey,
    areas: QueryKey,
    subareas: QueryKey,
    managerIDs: QueryKey
  ) => [
    Configs.platformGetCompanyContractsAPI,
    companyID,
    employeeID,
    'page',
    limit,
    cursor,
    employeeIDs,
    agreementIDs,
    areas,
    subareas,
    managerIDs,
  ],

  /** Prefix for every employees-without-active-contract page for this actor. */
  getEmployeesWithoutActiveContractPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetEmployeesWithoutActiveContractAPI,
    companyID,
    employeeID,
  ],

  getEmployeesWithoutActiveContract: (
    companyID: QueryKey,
    employeeID: QueryKey,
    limit: QueryKey,
    cursor: QueryKey,
    areas: QueryKey,
    subareas: QueryKey,
    employeeIDs: QueryKey,
    managerIDs: QueryKey
  ) => [
    Configs.platformGetEmployeesWithoutActiveContractAPI,
    companyID,
    employeeID,
    limit,
    cursor,
    areas,
    subareas,
    employeeIDs,
    managerIDs,
  ],

  /** Prefix for every employees-without-active-project page for this actor. */
  getEmployeesWithoutActiveProjectPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetEmployeesWithoutActiveProjectAPI,
    companyID,
    employeeID,
  ],

  getEmployeesWithoutActiveProject: (
    companyID: QueryKey,
    employeeID: QueryKey,
    limit: QueryKey,
    cursor: QueryKey,
    areas: QueryKey,
    subareas: QueryKey,
    employeeIDs: QueryKey,
    managerIDs: QueryKey
  ) => [
    Configs.platformGetEmployeesWithoutActiveProjectAPI,
    companyID,
    employeeID,
    limit,
    cursor,
    areas,
    subareas,
    employeeIDs,
    managerIDs,
  ],

  getCompanyAgreements: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetCompanyAgreementsAPI,
    companyID,
    employeeID,
  ],

  getCompanyAgreementTemplates: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetCompanyAgreementTemplatesAPI,
    companyID,
    employeeID,
  ],

  getCompanyAgreement: (companyID: QueryKey, employeeID: QueryKey, agreementID: QueryKey) => [
    Configs.platformGetCompanyAgreementAPI,
    companyID,
    employeeID,
    agreementID,
  ],

  adminSuperAdminCompanySummary: (
    companyID: QueryKey,
    employeeID: QueryKey,
    targetCompanyID: QueryKey
  ) => [Configs.platformAdminGetCompanySummaryAPI, companyID, employeeID, targetCompanyID],

  adminSuperAdminAgreementTemplates: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformAdminGetAgreementTemplatesAPI,
    companyID,
    employeeID,
  ],

  /** SuperAdmin all companies list (`platformAdminGetAllCompaniesAPI`). */
  adminAllCompanies: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformAdminGetAllCompaniesAPI,
    companyID,
    employeeID,
  ],

  /** SuperAdmin self-onboarding campaigns list. */
  adminSelfOnboardingCampaigns: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformAdminGetSelfOnboardingCampaignsAPI,
    companyID,
    employeeID,
  ],

  /** SuperAdmin self-onboarding campaign detail (`campaignID` or slug as lookup). */
  adminSelfOnboardingCampaign: (
    companyID: QueryKey,
    employeeID: QueryKey,
    campaignLookup: QueryKey
  ) => [
    Configs.platformAdminGetSelfOnboardingCampaignAPI,
    companyID,
    employeeID,
    campaignLookup,
  ],

  /** Platform country catalog for Admin create-company (`platformGetCountriesAPI`); `search` is trimmed for the key. */
  adminPlatformCountries: (companyID: QueryKey, employeeID: QueryKey, search: QueryKey) => [
    Configs.platformGetCountriesAPI,
    companyID,
    employeeID,
    search,
  ],

  getCompanyEngagement: (companyID: QueryKey, employeeID: QueryKey, filters: unknown) => [
    Configs.platformGetCompanyEngagementAPI,
    companyID,
    employeeID,
    filters,
  ],

  getCompanyDrivers: (companyID: QueryKey, employeeID: QueryKey, filters: unknown) => [
    Configs.platformGetCompanyDriversAPI,
    companyID,
    employeeID,
    filters,
  ],

  developmentDashboard: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    tableSegment: QueryKey,
    sortBy: QueryKey,
    sortDir: QueryKey
  ) => [
    Configs.platformPostDevelopmentDashboardAPI,
    companyID,
    employeeID,
    filtersKey,
    cursor ?? '',
    limit ?? '',
    tableSegment ?? '',
    sortBy ?? '',
    sortDir ?? '',
  ],

  /** Prefix: all dashboard queries for company + viewer (any filtersKey). */
  developmentDashboardPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformPostDevelopmentDashboardAPI,
    companyID,
    employeeID,
  ],

  developmentMemberPlans: (
    companyID: QueryKey,
    targetEmployeeID: QueryKey,
    filtersKey: QueryKey
  ) => [Configs.platformGetDevelopmentMemberPlansAPI, companyID, targetEmployeeID, filtersKey],

  developmentMemberPlansPrefix: (companyID: QueryKey, targetEmployeeID: QueryKey) => [
    Configs.platformGetDevelopmentMemberPlansAPI,
    companyID,
    targetEmployeeID,
  ],

  developmentPlansSharedWithViewer: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetDevelopmentPlansSharedWithViewerAPI,
    companyID,
    employeeID,
  ],

  developmentPlansSharedWithViewerPrefix: (companyID: QueryKey) => [
    Configs.platformGetDevelopmentPlansSharedWithViewerAPI,
    companyID,
  ],

  getGoals: (companyID: QueryKey, goalsEmployeeID: QueryKey, filtersKey: QueryKey) => [
    Configs.platformGetGoalsAPI,
    companyID,
    goalsEmployeeID,
    filtersKey,
  ],

  getGoalsPrefix: (companyID: QueryKey, goalsEmployeeID: QueryKey) => [
    Configs.platformGetGoalsAPI,
    companyID,
    goalsEmployeeID,
  ],

  goalsDashboard: (
    companyID: QueryKey,
    employeeID: QueryKey,
    targetEmployeeID: QueryKey,
    filtersKey: QueryKey,
    limit: QueryKey,
    cursor: QueryKey
  ) => [
    Configs.platformGetGoalsAPI,
    'dashboard',
    companyID,
    employeeID,
    targetEmployeeID,
    filtersKey,
    limit,
    cursor,
  ],

  goalsDashboardPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetGoalsAPI,
    'dashboard',
    companyID,
    employeeID,
  ],

  goalsCompany: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: QueryKey,
    limit: QueryKey,
    cursor: QueryKey
  ) => [
    Configs.platformGetGoalsAPI,
    'company',
    companyID,
    employeeID,
    filtersKey,
    limit,
    cursor,
  ],

  goalsCompanyPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetGoalsAPI,
    'company',
    companyID,
    employeeID,
  ],

  goalsTeam: (
    companyID: QueryKey,
    employeeID: QueryKey,
    visibility: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    countTotalKey: QueryKey,
    filtersKey: QueryKey
  ) => [
    Configs.platformGetGoalsTeamAPI,
    companyID,
    employeeID,
    visibility,
    cursor,
    limit,
    countTotalKey,
    filtersKey,
  ],

  goalsTeamPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetGoalsTeamAPI,
    companyID,
    employeeID,
  ],

  developmentTeam: (
    companyID: QueryKey,
    employeeID: QueryKey,
    visibility: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    countTotalKey: QueryKey,
    filtersKey: QueryKey
  ) => [
    Configs.platformGetDevelopmentTeamAPI,
    companyID,
    employeeID,
    visibility,
    cursor,
    limit,
    countTotalKey,
    filtersKey,
  ],

  developmentTeamPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetDevelopmentTeamAPI,
    companyID,
    employeeID,
  ],

  getPerformanceTeam: (
    companyID: QueryKey,
    employeeID: QueryKey,
    visibility: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    countTotalKey: QueryKey,
    filtersKey: QueryKey
  ) => [
    Configs.platformGetPerformanceTeamAPI,
    companyID,
    employeeID,
    visibility,
    cursor,
    limit,
    countTotalKey,
    filtersKey,
  ],

  getPerformanceTeamPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetPerformanceTeamAPI,
    companyID,
    employeeID,
  ],

  /** Prefix: invalidate all pages of HR Campaigns admin list. */
  getPerformanceCampaignsByCompanyPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetPerformanceReviewCampaignsByCompanyAPI,
    companyID,
    employeeID,
  ],

  /** Paginated performance campaigns for a company (HR Campaigns tab). */
  getPerformanceCampaignsByCompany: (
    companyID: QueryKey,
    employeeID: QueryKey,
    page: QueryKey = 1,
    limit: QueryKey = 20
  ) => [
    Configs.platformGetPerformanceReviewCampaignsByCompanyAPI,
    companyID,
    employeeID,
    page ?? 1,
    limit ?? 20,
  ],

  /**
   * Live performance campaigns for a subject (`GET livePerformanceCampaigns/`).
   * Keyed by subject company + employee so IC self and team-member routes do not share stale data.
   */
  getLivePerformanceCampaigns: (
    companyID: QueryKey,
    employeeID: QueryKey,
    subjectCompanyID: QueryKey,
    subjectEmployeeID: QueryKey
  ) => [
    Configs.platformGetLivePerformanceCampaignsAPI,
    companyID,
    employeeID,
    subjectCompanyID,
    subjectEmployeeID,
  ],

  /** Prefix: invalidate live campaigns for any subject under this actor. */
  getLivePerformanceCampaignsPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetLivePerformanceCampaignsAPI,
    companyID,
    employeeID,
  ],

  /**
   * Feedback providers for a subject in a campaign (`GET performanceFeedbackProviders/`).
   * `subjectEmployeeID` is the employee under review (self or team member).
   */
  getPerformanceFeedbackProviders: (
    companyID: QueryKey,
    employeeID: QueryKey,
    subjectEmployeeID: QueryKey,
    campaignID: QueryKey
  ) => [
    Configs.platformGetPerformanceFeedbackProvidersAPI,
    companyID,
    employeeID,
    subjectEmployeeID,
    campaignID,
  ],

  /** Prefix: invalidate providers for a subject across campaigns. */
  getPerformanceFeedbackProvidersPrefix: (
    companyID: QueryKey,
    employeeID: QueryKey,
    subjectEmployeeID: QueryKey
  ) => [
    Configs.platformGetPerformanceFeedbackProvidersAPI,
    companyID,
    employeeID,
    subjectEmployeeID,
  ],

  /**
   * Self / IC performance draft (`GET performanceDraft/`).
   * `subjectEmployeeID` is the employee under review.
   */
  getPerformanceSelfDraft: (
    companyID: QueryKey,
    employeeID: QueryKey,
    subjectEmployeeID: QueryKey,
    campaignID: QueryKey
  ) => [
    Configs.platformGetPerformanceDraftsAPI,
    companyID,
    employeeID,
    subjectEmployeeID,
    campaignID,
  ],

  getPerformanceSelfDraftPrefix: (
    companyID: QueryKey,
    employeeID: QueryKey,
    subjectEmployeeID: QueryKey
  ) => [
    Configs.platformGetPerformanceDraftsAPI,
    companyID,
    employeeID,
    subjectEmployeeID,
  ],

  /**
   * Manager performance draft (`GET managerPerformanceDraft/`).
   */
  getPerformanceManagerDraft: (
    companyID: QueryKey,
    employeeID: QueryKey,
    subjectEmployeeID: QueryKey,
    campaignID: QueryKey,
    managerID: QueryKey
  ) => [
    Configs.platformGetManagerPerformanceDraftsAPI,
    companyID,
    employeeID,
    subjectEmployeeID,
    campaignID,
    managerID,
  ],

  getPerformanceManagerDraftPrefix: (
    companyID: QueryKey,
    employeeID: QueryKey,
    subjectEmployeeID: QueryKey
  ) => [
    Configs.platformGetManagerPerformanceDraftsAPI,
    companyID,
    employeeID,
    subjectEmployeeID,
  ],

  /**
   * Peer feedback drafts for a subject (`GET performanceFeedbacksDrafts/`).
   */
  getPerformanceFeedbacksDrafts: (
    companyID: QueryKey,
    employeeID: QueryKey,
    subjectEmployeeID: QueryKey,
    campaignID: QueryKey
  ) => [
    Configs.platformGetPerformanceFeedbacksDraftsAPI,
    companyID,
    employeeID,
    subjectEmployeeID,
    campaignID,
  ],

  getPerformanceFeedbacksDraftsPrefix: (
    companyID: QueryKey,
    employeeID: QueryKey,
    subjectEmployeeID: QueryKey
  ) => [
    Configs.platformGetPerformanceFeedbacksDraftsAPI,
    companyID,
    employeeID,
    subjectEmployeeID,
  ],

  /**
   * Discuss review waterfall (sent → completed → submitted) for a subject.
   */
  getPerformanceDiscussReview: (
    companyID: QueryKey,
    employeeID: QueryKey,
    subjectCompanyID: QueryKey,
    subjectEmployeeID: QueryKey
  ) => [
    Configs.platformGetPerformanceReviewByStatusAPI,
    'discuss',
    companyID,
    employeeID,
    subjectCompanyID,
    subjectEmployeeID,
  ],

  getPerformanceDiscussReviewPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetPerformanceReviewByStatusAPI,
    'discuss',
    companyID,
    employeeID,
  ],

  careerTeam: (
    companyID: QueryKey,
    employeeID: QueryKey,
    visibility: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    countTotalKey: QueryKey,
    filtersKey: QueryKey
  ) => [
    Configs.platformGetCareerTeamAPI,
    companyID,
    employeeID,
    visibility,
    cursor,
    limit,
    countTotalKey,
    filtersKey,
  ],

  careerTeamPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetCareerTeamAPI,
    companyID,
    employeeID,
  ],

  /** Company job-levelling guidelines list (`GET careerJobGuidelines/`). */
  careerJobGuidelines: (companyID: QueryKey) => [
    Configs.platformGetCareerJobGuidelinesAPI,
    companyID,
  ],

  careerReportingRoster: (
    companyID: QueryKey,
    employeeID: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    countTotalKey: QueryKey,
    filtersKey: QueryKey,
    sortByKey: QueryKey = '',
    sortDirKey: QueryKey = ''
  ) => [
    Configs.platformGetCareerReportingRosterAPI,
    companyID,
    employeeID,
    cursor,
    limit,
    countTotalKey,
    filtersKey,
    sortByKey,
    sortDirKey,
  ],

  careerReportingRosterPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetCareerReportingRosterAPI,
    companyID,
    employeeID,
  ],

  careerReportingPathCoverage: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: QueryKey
  ) => [
    Configs.platformGetCareerReportingPathCoverageAPI,
    companyID,
    employeeID,
    filtersKey,
  ],

  careerReportingPathCoveragePrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetCareerReportingPathCoverageAPI,
    companyID,
    employeeID,
  ],

  careerReportingActivityFreshness: (
    companyID: QueryKey,
    employeeID: QueryKey,
    staleAfterDays: QueryKey,
    filtersKey: QueryKey
  ) => [
    Configs.platformGetCareerReportingActivityFreshnessAPI,
    companyID,
    employeeID,
    staleAfterDays,
    filtersKey,
  ],

  careerReportingActivityFreshnessPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetCareerReportingActivityFreshnessAPI,
    companyID,
    employeeID,
  ],

  oneOnOneTeam: (
    companyID: QueryKey,
    employeeID: QueryKey,
    visibility: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    countTotalKey: QueryKey,
    filtersKey: QueryKey
  ) => [
    Configs.platformGetOneOnOneTeamAPI,
    companyID,
    employeeID,
    visibility,
    cursor,
    limit,
    countTotalKey,
    filtersKey,
  ],

  oneOnOneTeamPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetOneOnOneTeamAPI,
    companyID,
    employeeID,
  ],

  developmentPlanById: (companyID: QueryKey, employeeID: QueryKey, planID: QueryKey) => [
    Configs.platformGetDevelopmentPlanByIDAPI,
    companyID,
    employeeID,
    planID,
  ],

  developmentPlanUpdates: (companyID: QueryKey, employeeID: QueryKey, planID: QueryKey) => [
    Configs.platformGetDevelopmentPlanUpdatesAPI,
    companyID,
    employeeID,
    planID,
  ],

  /** Manager roster capabilities bootstrap (`platformGetEmployeeVisibilityScopeCapabilitiesAPI`). */
  employeeVisibilityScopeCapabilities: (
    companyID: QueryKey,
    employeeID: QueryKey,
    companyDirectoryKey: QueryKey = 0
  ) => [
    Configs.platformGetEmployeeVisibilityScopeCapabilitiesAPI,
    companyID,
    employeeID,
    companyDirectoryKey,
  ],

  /**
   * Prefix for all paginated roster queries for this actor — covers organigram list and
   * Learning team (both use `useGetEmployeeVisibilityScope`). TanStack matches every key
   * that starts with this triple regardless of page cursor / filter segments.
   */
  allEmployeeVisibilityScopeForActor: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetEmployeeVisibilityScopeAPI,
    companyID,
    employeeID,
  ] as const,

  /**
   * Paginated manager roster (`platformGetEmployeeVisibilityScopeAPI`).
   * `filtersKey` should stabilize organigram filter dimensions (areas, subareas, search, etc.).
   */
  employeeVisibilityScope: (
    companyID: QueryKey,
    employeeID: QueryKey,
    visibility: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    countTotalKey: QueryKey,
    filtersKey: QueryKey,
    companyDirectoryKey: QueryKey = 0
  ) => [
    Configs.platformGetEmployeeVisibilityScopeAPI,
    companyID,
    employeeID,
    visibility,
    cursor,
    limit,
    countTotalKey,
    filtersKey,
    companyDirectoryKey,
  ],

  /** Visibility-scoped areas facet (`platformGetEmployeeVisibilityScopeFiltersAreasAPI`). */
  employeeVisibilityScopeFiltersAreas: (
    companyID: QueryKey,
    employeeID: QueryKey,
    visibility: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    companyDirectoryKey: QueryKey = 0
  ) => [
    Configs.platformGetEmployeeVisibilityScopeFiltersAreasAPI,
    companyID,
    employeeID,
    visibility,
    search,
    cursor,
    limit,
    companyDirectoryKey,
  ],

  /** Visibility-scoped subareas facet (`platformGetEmployeeVisibilityScopeFiltersSubareasAPI`). */
  employeeVisibilityScopeFiltersSubareas: (
    companyID: QueryKey,
    employeeID: QueryKey,
    visibility: QueryKey,
    search: QueryKey,
    area: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    companyDirectoryKey: QueryKey = 0
  ) => [
    Configs.platformGetEmployeeVisibilityScopeFiltersSubareasAPI,
    companyID,
    employeeID,
    visibility,
    search,
    area,
    cursor,
    limit,
    companyDirectoryKey,
  ],

  /** Visibility-scoped employees facet (`platformGetEmployeeVisibilityScopeFiltersEmployeesAPI`). */
  employeeVisibilityScopeFiltersEmployees: (
    companyID: QueryKey,
    employeeID: QueryKey,
    visibility: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    companyDirectoryKey: QueryKey = 0
  ) => [
    Configs.platformGetEmployeeVisibilityScopeFiltersEmployeesAPI,
    companyID,
    employeeID,
    visibility,
    search,
    cursor,
    limit,
    companyDirectoryKey,
  ],

  /** Visibility-scoped managers facet (`platformGetEmployeeVisibilityScopeFiltersManagersAPI`). */
  employeeVisibilityScopeFiltersManagers: (
    companyID: QueryKey,
    employeeID: QueryKey,
    visibility: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    companyDirectoryKey: QueryKey = 0
  ) => [
    Configs.platformGetEmployeeVisibilityScopeFiltersManagersAPI,
    companyID,
    employeeID,
    visibility,
    search,
    cursor,
    limit,
    companyDirectoryKey,
  ],

  /** Prefix: invalidate all areas facet queries for this actor (any visibility / search / cursor). */
  allEmployeeVisibilityScopeFiltersAreasForActor: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetEmployeeVisibilityScopeFiltersAreasAPI,
    companyID,
    employeeID,
  ] as const,

  /** Prefix: invalidate all subareas facet queries for this actor (any visibility / search / cursor). */
  allEmployeeVisibilityScopeFiltersSubareasForActor: (
    companyID: QueryKey,
    employeeID: QueryKey
  ) => [
    Configs.platformGetEmployeeVisibilityScopeFiltersSubareasAPI,
    companyID,
    employeeID,
  ] as const,

  /** Prefix: invalidate all employees facet queries for this actor (any visibility / search / cursor). */
  allEmployeeVisibilityScopeFiltersEmployeesForActor: (
    companyID: QueryKey,
    employeeID: QueryKey
  ) => [
    Configs.platformGetEmployeeVisibilityScopeFiltersEmployeesAPI,
    companyID,
    employeeID,
  ] as const,

  /** Prefix: invalidate all managers facet queries for this actor (any visibility / search / cursor). */
  allEmployeeVisibilityScopeFiltersManagersForActor: (
    companyID: QueryKey,
    employeeID: QueryKey
  ) => [
    Configs.platformGetEmployeeVisibilityScopeFiltersManagersAPI,
    companyID,
    employeeID,
  ] as const,

  /** Company punch projects list (`platformGetCompanyProjectsAPI`). */
  getPunchCompanyProjects: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetCompanyProjectsAPI,
    companyID,
    employeeID,
  ],

  /** Company employee directory for punch/settings (`platformGetEmployeesOfCompanyAPI`). */
  getPunchEmployeesOfCompany: (
    companyID: QueryKey,
    employeeID: QueryKey,
    onlyActive: 'true' | 'false'
  ) => [Configs.platformGetEmployeesOfCompanyAPI, companyID, employeeID, onlyActive],

  /**
   * Prefix: invalidate every company employee directory query for this actor (both
   * `onlyActive: 'true'` and `'false'` variants), so punch/HR selectors refresh
   * immediately after an employee is added, updated, or removed.
   */
  getPunchEmployeesOfCompanyPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetEmployeesOfCompanyAPI,
    companyID,
    employeeID,
  ] as const,

  /** Single employee basic info row (`platformGetEmployeeBasicInfoAPI`). */
  getEmployeeBasicInfo: (companyID: QueryKey, targetEmployeeID: QueryKey) => [
    Configs.platformGetEmployeeBasicInfoAPI,
    companyID,
    targetEmployeeID,
  ],

  /**
   * Authenticated user's own self-profile for Career module, including `careerInfo`.
   * Distinct from `getEmployeeProfile` to avoid shape conflicts (full body vs. extracted payload).
   */
  careerSelfProfile: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetProfileAPI,
    'career-self',
    companyID,
    employeeID,
  ],

  /** Single employee profile (`platformGetProfileAPI`). */
  getEmployeeProfile: (companyID: QueryKey, targetEmployeeID: QueryKey) => [
    Configs.platformGetProfileAPI,
    companyID,
    targetEmployeeID,
  ],

  complianceCatalog: (
    companyID: QueryKey,
    employeeID: QueryKey,
    countryCode: QueryKey = ''
  ) => [
    Configs.platformGetComplianceCatalogAPI,
    companyID,
    employeeID,
    countryCode,
  ],

  complianceCompany: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetComplianceCompanyAPI,
    companyID,
    employeeID,
  ],

  complianceScore: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetComplianceScoreAPI,
    companyID,
    employeeID,
  ],

  compliancePolicies: (
    companyID: QueryKey,
    employeeID: QueryKey,
    countryCode: QueryKey
  ) => [
    Configs.platformGetCompliancePoliciesAPI,
    companyID,
    employeeID,
    countryCode,
  ],

  compliancePolicyItem: (
    companyID: QueryKey,
    employeeID: QueryKey,
    countryCode: QueryKey,
    requirementKey: QueryKey
  ) => [
    Configs.platformGetCompliancePolicyItemAPI,
    companyID,
    employeeID,
    countryCode,
    requirementKey,
  ],

  complianceMyDocuments: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetComplianceMyDocumentsAPI,
    companyID,
    employeeID,
  ],

  /** Authenticated employee preferred language (`platformGetEmployeePreferredLanguageAPI`). */
  getEmployeePreferredLanguage: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetEmployeePreferredLanguageAPI,
    companyID,
    employeeID,
  ],

  /** Company preferences / module config (`platformGetCompanyPreferencesAPI`). */
  getCompanyPreferences: (companyID: QueryKey) => [
    Configs.platformGetCompanyPreferencesAPI,
    companyID,
  ],

  /**
   * Career subject profile for a target employee (`platformGetCareerSubjectProfileAPI`).
   * Enabled only when viewer is authorised (Manager direct-report, HR/Exec/Admin, HRBP, or self).
   */
  careerSubjectProfile: (companyID: QueryKey, employeeID: QueryKey, targetEmployeeID: QueryKey) => [
    Configs.platformGetCareerSubjectProfileAPI,
    companyID,
    employeeID,
    targetEmployeeID,
  ],

  /** KAI company document index (`platformGetKAICompanyPreferencesAPI`). */
  getCompanyKaiSettings: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetKAICompanyPreferencesAPI,
    companyID,
    employeeID,
  ],

  /**
   * Paginated company employees / org directory (`platformGetCompanyEmployeesPageAPI`).
   */
  companyEmployeesPage: (
    companyID: QueryKey,
    employeeID: QueryKey,
    /** Trimmed search string (empty when unset). */
    search: QueryKey,
    /** Serialized multivalue wall filters (same semantics as {@link buildRecognitionWallFiltersQueryKeyPart}). */
    wallFiltersKey: string,
    cursor: QueryKey,
    limit: QueryKey,
    countTotal: QueryKey
  ) => [
    Configs.platformGetCompanyEmployeesPageAPI,
    companyID,
    employeeID,
    search,
    wallFiltersKey,
    cursor,
    limit,
    countTotal,
  ],

  /** Invalidate all company employee page queries for this actor (any filters / cursor). */
  allCompanyEmployeesPagesForActor: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetCompanyEmployeesPageAPI,
    companyID,
    employeeID,
  ],

  /** Company filter facets for HR / settings org filters (`platformGetCompanyFiltersAPI`). */
  getCompanyFilters: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetCompanyFiltersAPI,
    companyID,
    employeeID,
  ],

  // ── Filters module — paginated, scope-aware lookups ──────────────────────

  /** Paginated employees filter (`platformGetFiltersEmployeesAPI`). */
  filtersEmployees: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    manager: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [
    Configs.platformGetFiltersEmployeesAPI,
    companyID,
    employeeID,
    search,
    manager,
    cursor,
    limit,
    scopeKey,
  ],

  /** Paginated managers filter (`platformGetFiltersManagersAPI`). */
  filtersManagers: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [
    Configs.platformGetFiltersManagersAPI,
    companyID,
    employeeID,
    search,
    cursor,
    limit,
    scopeKey,
  ],

  /** Paginated areas filter (`platformGetFiltersAreasAPI`). */
  filtersAreas: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [Configs.platformGetFiltersAreasAPI, companyID, employeeID, search, cursor, limit, scopeKey],

  /** Paginated subareas filter (`platformGetFiltersSubareasAPI`). */
  filtersSubareas: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    area: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [
    Configs.platformGetFiltersSubareasAPI,
    companyID,
    employeeID,
    search,
    area,
    cursor,
    limit,
    scopeKey,
  ],

  filtersTitles: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [
    Configs.platformGetFiltersTitlesAPI,
    companyID,
    employeeID,
    search,
    cursor,
    limit,
    scopeKey,
  ],

  filtersRoles: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [Configs.platformGetFiltersRolesAPI, companyID, employeeID, search, cursor, limit, scopeKey],

  filtersLevels: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [
    Configs.platformGetFiltersLevelsAPI,
    companyID,
    employeeID,
    search,
    cursor,
    limit,
    scopeKey,
  ],

  filtersTenure: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [
    Configs.platformGetFiltersTenureAPI,
    companyID,
    employeeID,
    search,
    cursor,
    limit,
    scopeKey,
  ],

  filtersTypes: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [
    Configs.platformGetFiltersTypesAPI,
    companyID,
    employeeID,
    search,
    cursor,
    limit,
    scopeKey,
  ],

  filtersLegalEntities: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [
    Configs.platformGetFiltersLegalEntitiesAPI,
    companyID,
    employeeID,
    search,
    cursor,
    limit,
    scopeKey,
  ],

  filtersLegalGenders: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [
    Configs.platformGetFiltersLegalGendersAPI,
    companyID,
    employeeID,
    search,
    cursor,
    limit,
    scopeKey,
  ],

  filtersContractTypes: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [
    Configs.platformGetFiltersContractTypesAPI,
    companyID,
    employeeID,
    search,
    cursor,
    limit,
    scopeKey,
  ],

  filtersPlacesOfWork: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [
    Configs.platformGetFiltersPlacesOfWorkAPI,
    companyID,
    employeeID,
    search,
    cursor,
    limit,
    scopeKey,
  ],

  filtersCountriesOfWork: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [
    Configs.platformGetFiltersCountriesOfWorkAPI,
    companyID,
    employeeID,
    search,
    cursor,
    limit,
    scopeKey,
  ],

  filtersCitiesOfWork: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [
    Configs.platformGetFiltersCitiesOfWorkAPI,
    companyID,
    employeeID,
    search,
    cursor,
    limit,
    scopeKey,
  ],

  filtersCountriesOfOperations: (
    companyID: QueryKey,
    employeeID: QueryKey,
    search: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    scopeKey: QueryKey = ''
  ) => [
    Configs.platformGetFiltersCountriesOfOperationsAPI,
    companyID,
    employeeID,
    search,
    cursor,
    limit,
    scopeKey,
  ],

  /**
   * HR pending timesheets list page (`platformGetPunchInOutCompanyPendingTimesheetAPI`).
   * Include every query input that changes the response (week, filters, cursor, limit, countTotal).
   */
  punchPendingTimesheetsPage: (
    companyID: QueryKey,
    employeeID: QueryKey,
    weekNumeric: QueryKey,
    area: QueryKey,
    subarea: QueryKey,
    manager: QueryKey,
    status: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    countTotal: QueryKey
  ) => [
    Configs.platformGetPunchInOutCompanyPendingTimesheetAPI,
    companyID,
    employeeID,
    weekNumeric,
    area,
    subarea,
    manager,
    status,
    cursor,
    limit,
    countTotal,
  ],

  /** Invalidate all pending-timesheet list queries for this actor (any page / filters). */
  allPunchPendingTimesheetsForActor: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetPunchInOutCompanyPendingTimesheetAPI,
    companyID,
    employeeID,
  ],

  /**
   * Onboarding HR dashboard eligible roster (`platformGetOnboardingEligibleCandidatesAPI`).
   * Full key includes EVS visibility / cursor / limit / filters / sort.
   */
  getOnboardingEligibleCandidates: (
    companyID: QueryKey,
    employeeID: QueryKey,
    visibility: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    countTotalKey: QueryKey,
    filtersKey: QueryKey,
    sortBy: QueryKey,
    sortDir: QueryKey
  ) => [
    Configs.platformGetOnboardingEligibleCandidatesAPI,
    companyID,
    employeeID,
    visibility,
    cursor,
    limit,
    countTotalKey,
    filtersKey,
    sortBy,
    sortDir,
  ],

  /** Prefix: invalidate all eligible-candidates pages for an actor. */
  getOnboardingEligibleCandidatesPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetOnboardingEligibleCandidatesAPI,
    companyID,
    employeeID,
  ],

  /** Lean people options for buddy / task owners (`platformGetOnboardingPeopleOptionsAPI`). */
  getOnboardingPeopleOptions: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetOnboardingPeopleOptionsAPI,
    companyID,
    employeeID,
  ],

  /** Single subject hydrate for plan editors (`platformGetOnboardingSubjectProfileAPI`). */
  getOnboardingSubjectProfile: (companyID: QueryKey, targetEmployeeID: QueryKey) => [
    Configs.platformGetOnboardingSubjectProfileAPI,
    companyID,
    targetEmployeeID,
  ],

  /** Single employee onboarding plan (`platformGetOnboardingPlanForEmployeeAPI`). */
  getOnboardingPlanForEmployee: (companyID: QueryKey, targetEmployeeID: QueryKey) => [
    Configs.platformGetOnboardingPlanForEmployeeAPI,
    companyID,
    targetEmployeeID,
  ],

  /** Onboarding plan templates (`platformGetOnboardingPlanTemplatesAPI`). */
  getOnboardingPlanTemplates: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetOnboardingPlanTemplatesAPI,
    companyID,
    employeeID,
  ],

  /** Development plan templates (`platformGetDevelopmentPlanTemplatesAPI`). */
  getDevelopmentPlanTemplates: (
    companyID: QueryKey,
    employeeID: QueryKey,
    cursor: QueryKey = '',
    limit: QueryKey = '',
    area: QueryKey = '',
    subarea: QueryKey = '',
    search: QueryKey = '',
    scope: QueryKey = '',
    sortBy: QueryKey = '',
    sortDir: QueryKey = '',
    targetEmployeeID: QueryKey | null = null,
    templateID: QueryKey | null = null
  ) => [
    Configs.platformGetDevelopmentPlanTemplatesAPI,
    companyID,
    employeeID,
    cursor ?? '',
    limit ?? '',
    area ?? '',
    subarea ?? '',
    search ?? '',
    scope ?? '',
    sortBy ?? '',
    sortDir ?? '',
    targetEmployeeID,
    templateID,
  ],

  /** Prefix for all development plan template queries for this actor. */
  allDevelopmentPlanTemplatesForActor: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetDevelopmentPlanTemplatesAPI,
    companyID,
    employeeID,
  ],

  /** Onboarding reporting dataset (`platformGetOnboardingReportingDataAPI`). */
  getOnboardingReportingData: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetOnboardingReportingDataAPI,
    companyID,
    employeeID,
  ],

  /** Onboarding Analytics status funnel (`platformGetOnboardingReportingStatusFunnelAPI`). */
  onboardingReportingStatusFunnel: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: QueryKey
  ) => [
    Configs.platformGetOnboardingReportingStatusFunnelAPI,
    companyID,
    employeeID,
    filtersKey,
  ],

  onboardingReportingStatusFunnelPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetOnboardingReportingStatusFunnelAPI,
    companyID,
    employeeID,
  ],

  /** Onboarding Analytics completion (`platformGetOnboardingReportingCompletionAPI`). */
  onboardingReportingCompletion: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: QueryKey
  ) => [
    Configs.platformGetOnboardingReportingCompletionAPI,
    companyID,
    employeeID,
    filtersKey,
  ],

  onboardingReportingCompletionPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetOnboardingReportingCompletionAPI,
    companyID,
    employeeID,
  ],

  /** Onboarding Analytics aging (`platformGetOnboardingReportingAgingAPI`). */
  onboardingReportingAging: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: QueryKey
  ) => [
    Configs.platformGetOnboardingReportingAgingAPI,
    companyID,
    employeeID,
    filtersKey,
  ],

  onboardingReportingAgingPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetOnboardingReportingAgingAPI,
    companyID,
    employeeID,
  ],

  /** Onboarding Analytics buddy coverage (`platformGetOnboardingReportingBuddyCoverageAPI`). */
  onboardingReportingBuddyCoverage: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: QueryKey
  ) => [
    Configs.platformGetOnboardingReportingBuddyCoverageAPI,
    companyID,
    employeeID,
    filtersKey,
  ],

  onboardingReportingBuddyCoveragePrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetOnboardingReportingBuddyCoverageAPI,
    companyID,
    employeeID,
  ],

  /** Onboarding Analytics survey themes (`platformGetOnboardingReportingSurveyThemesAPI`). */
  onboardingReportingSurveyThemes: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: QueryKey
  ) => [
    Configs.platformGetOnboardingReportingSurveyThemesAPI,
    companyID,
    employeeID,
    filtersKey,
  ],

  onboardingReportingSurveyThemesPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetOnboardingReportingSurveyThemesAPI,
    companyID,
    employeeID,
  ],

  /** Onboarding Analytics Likert distribution (`platformGetOnboardingReportingLikertDistributionAPI`). */
  onboardingReportingLikertDistribution: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: QueryKey
  ) => [
    Configs.platformGetOnboardingReportingLikertDistributionAPI,
    companyID,
    employeeID,
    filtersKey,
  ],

  onboardingReportingLikertDistributionPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetOnboardingReportingLikertDistributionAPI,
    companyID,
    employeeID,
  ],

  /**
   * Organigram tree for the company (`platformGetOrganigramAPI`).
   * Include `modeOrFingerprint` so lazy snapshots and filter JSON invalidate independently.
   */
  getOrganigram: (
    companyID: QueryKey,
    employeeID: QueryKey,
    modeOrFingerprint: QueryKey = 'lazy:2:none'
  ) => [Configs.platformGetOrganigramAPI, companyID, employeeID, modeOrFingerprint],

  /** Prefix for all organigram snapshots (every depth/filter fingerprint for this actor). */
  allOrganigramForActor: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetOrganigramAPI,
    companyID,
    employeeID,
  ],

  /** Employee permission grants for a target employee (`platformGetEmployeePermissionsAPI`). */
  getEmployeePermissionGrants: (
    companyID: QueryKey,
    employeeID: QueryKey,
    targetEmployeeID: QueryKey
  ) => [Configs.platformGetEmployeePermissionsAPI, companyID, employeeID, targetEmployeeID],

  /** Team holiday calendar for managers / HR / exec (`platformGetTeamHolidayCalendarAPI`). */
  getTeamHolidayCalendar: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetTeamHolidayCalendarAPI,
    companyID,
    employeeID,
  ],

  /**
   * Paged HR time-off report (`platformGetDaysOffHRReportAPI`).
   * `limit` selects the page response. Callers that omit it still receive the full array.
   */
  getDaysOffHRReport: (
    companyID: QueryKey,
    employeeID: QueryKey,
    limit: QueryKey,
    cursor: QueryKey,
    areas: QueryKey,
    subareas: QueryKey,
    employeeIDs: QueryKey,
    managerIDs: QueryKey
  ) => [
    Configs.platformGetDaysOffHRReportAPI,
    companyID,
    employeeID,
    limit,
    cursor,
    areas,
    subareas,
    employeeIDs,
    managerIDs,
  ],

  /**
   * Paginated HR 1:1 compliance report (`platformGetOneOnOnesReportAPI`).
   */
  oneOnOnesReport: (
    companyID: QueryKey,
    employeeID: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    filtersKey: QueryKey,
    timeframeKey: QueryKey,
    sortBy: QueryKey,
    sortDir: QueryKey
  ) => [
    Configs.platformGetOneOnOnesReportAPI,
    companyID,
    employeeID,
    cursor,
    limit,
    filtersKey,
    timeframeKey,
    sortBy,
    sortDir,
  ],

  /** HR 1:1 compliance KPIs (`platformGetOneOnOnesReportMetricsAPI`). */
  oneOnOnesReportMetrics: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: QueryKey,
    timeframeKey: QueryKey
  ) => [
    Configs.platformGetOneOnOnesReportMetricsAPI,
    companyID,
    employeeID,
    filtersKey,
    timeframeKey,
  ],

  oneOnOnesReportPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetOneOnOnesReportAPI,
    companyID,
    employeeID,
  ],

  /**
   * Manager-wide 1:1 history timeline (`platformGetOneOnOneHistoryManagerAPI`).
   */
  oneOnOneHistory: (
    companyID: QueryKey,
    managerID: QueryKey,
    cursor: QueryKey,
    limit: QueryKey
  ) => [
    Configs.platformGetOneOnOneHistoryManagerAPI,
    companyID,
    managerID,
    cursor,
    limit,
  ],

  oneOnOneHistoryPrefix: (companyID: QueryKey, managerID: QueryKey) => [
    Configs.platformGetOneOnOneHistoryManagerAPI,
    companyID,
    managerID,
  ],

  /**
   * Employee 1:1 space timeline (`platformGetMeetingsByEmployeeAPI`).
   */
  oneOnOneSpace: (
    companyID: QueryKey,
    targetEmployeeID: QueryKey,
    managerID: QueryKey,
    cursor: QueryKey,
    limit: QueryKey
  ) => [
    Configs.platformGetMeetingsByEmployeeAPI,
    companyID,
    targetEmployeeID,
    managerID,
    cursor,
    limit,
  ],

  oneOnOneSpacePrefix: (companyID: QueryKey, targetEmployeeID: QueryKey) => [
    Configs.platformGetMeetingsByEmployeeAPI,
    companyID,
    targetEmployeeID,
  ],

  /**
   * Single 1:1 meeting editor (`platformGetMeetingAPI`).
   */
  oneOnOneMeeting: (
    companyID: QueryKey,
    callerEmployeeID: QueryKey,
    targetEmployeeID: QueryKey,
    managerID: QueryKey,
    meetingID: QueryKey
  ) => [
    Configs.platformGetMeetingAPI,
    companyID,
    callerEmployeeID,
    targetEmployeeID,
    managerID,
    meetingID,
  ],

  /**
   * One page of company recognition (`platformGetRecognitionDataForCompanyPageAPI`).
   * Include every query param that changes the response in the key.
   */
  recognitionDataForCompanyPage: (
    companyID: QueryKey,
    employeeID: QueryKey,
    cursor: string | null,
    limit: QueryKey,
    countTotalKey: string | number,
    /** Serialized server wall filters (must match GET query string semantics). */
    wallFiltersKey: string,
    /** Serialized wall timeframe (must match GET query string semantics). */
    wallTimeframeKey: string
  ) => [
    Configs.platformGetRecognitionDataForCompanyPageAPI,
    companyID,
    employeeID,
    cursor ?? '',
    limit,
    countTotalKey,
    wallFiltersKey,
    wallTimeframeKey,
  ],

  /**
   * Prefix for all paginated recognition list queries for this actor (invalidate after writes).
   */
  allRecognitionDataForCompanyPages: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetRecognitionDataForCompanyPageAPI,
    companyID,
    employeeID,
  ],

  /**
   * Personal recognition summary for profile / 1:1 widget (`platformGetRecognitionPersonalSummaryAPI`).
   */
  recognitionPersonalSummary: (
    companyID: QueryKey,
    requesterEmployeeID: QueryKey,
    targetEmployeeID: QueryKey
  ) => [
    Configs.platformGetRecognitionPersonalSummaryAPI,
    companyID,
    requesterEmployeeID,
    targetEmployeeID,
  ],

  /** Prefix: invalidate every personal summary for this requester (e.g. after POST recognition). */
  allRecognitionPersonalSummaries: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetRecognitionPersonalSummaryAPI,
    companyID,
    employeeID,
  ],

  /**
   * One page of company recognition leaderboards (`platformGetRecognitionLeaderboardsForCompanyPageAPI`).
   */
  recognitionLeaderboardsForCompanyPage: (
    companyID: QueryKey,
    employeeID: QueryKey,
    cursor: string | null,
    limit: QueryKey,
    countTotalKey: string | number,
    /** Serialized server wall filters (must match GET query string semantics). */
    wallFiltersKey: string,
    /** `sortBy:sortDir` or empty when using API default sort. */
    sortKeyPart?: string
  ) => [
    Configs.platformGetRecognitionLeaderboardsForCompanyPageAPI,
    companyID,
    employeeID,
    cursor ?? '',
    limit,
    countTotalKey,
    wallFiltersKey,
    sortKeyPart ?? '',
  ],

  /** Prefix: invalidate every paginated leaderboard query for this actor (e.g. after POST recognition). */
  allRecognitionLeaderboardsForCompanyPages: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetRecognitionLeaderboardsForCompanyPageAPI,
    companyID,
    employeeID,
  ],

  recognitionReportingSummary: (companyID: QueryKey, employeeID: QueryKey, contextKey: string) => [
    Configs.platformPostRecognitionReportingSummaryAPI,
    companyID,
    employeeID,
    contextKey,
  ],

  recognitionReportingSegment: (
    companyID: QueryKey,
    employeeID: QueryKey,
    contextKey: string,
    groupBy: string
  ) => [
    Configs.platformPostRecognitionReportingSegmentAPI,
    companyID,
    employeeID,
    contextKey,
    groupBy,
  ],

  recognitionReportingAreaHighlights: (
    companyID: QueryKey,
    employeeID: QueryKey,
    contextKey: string
  ) => [
    Configs.platformPostRecognitionReportingAreaHighlightsAPI,
    companyID,
    employeeID,
    contextKey,
  ],

  recognitionReportingByValues: (companyID: QueryKey, employeeID: QueryKey, contextKey: string) => [
    Configs.platformPostRecognitionReportingByValuesAPI,
    companyID,
    employeeID,
    contextKey,
  ],

  recognitionReportingManagerAdoption: (
    companyID: QueryKey,
    employeeID: QueryKey,
    contextKey: string
  ) => [
    Configs.platformPostRecognitionReportingManagerAdoptionAPI,
    companyID,
    employeeID,
    contextKey,
  ],

  recognitionReportingEmployeeSpotlights: (
    companyID: QueryKey,
    employeeID: QueryKey,
    contextKey: string
  ) => [
    Configs.platformPostRecognitionReportingEmployeeSpotlightsAPI,
    companyID,
    employeeID,
    contextKey,
  ],

  recognitionReportingNetwork: (
    companyID: QueryKey,
    employeeID: QueryKey,
    contextKey: string,
    groupBy: string
  ) => [
    Configs.platformPostRecognitionReportingNetworkAPI,
    companyID,
    employeeID,
    contextKey,
    groupBy,
  ],

  recognitionReportingEmployees: (
    companyID: QueryKey,
    employeeID: QueryKey,
    contextKey: string,
    cursor: string | null,
    limit: QueryKey,
    countTotalKey: string | number,
    sortKeyPart: string
  ) => [
    Configs.platformGetRecognitionReportingEmployeesAPI,
    companyID,
    employeeID,
    contextKey,
    cursor ?? '',
    limit,
    countTotalKey,
    sortKeyPart,
  ],

  /** Company rewards catalog (`platformGetRewardsDataForCompanyAPI`). */
  getRewardsDataForCompany: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetRewardsDataForCompanyAPI,
    companyID,
    employeeID,
  ],

  /** Public single recognition by id (`platformGetPublicSingleRecognitionAPI`). */
  getPublicSingleRecognition: (recognitionID: QueryKey) => [
    Configs.platformGetPublicSingleRecognitionAPI,
    recognitionID,
  ],

  /** Pulse Home — sidebar facet options (`platformPostPulseHomeAvailableFiltersAPI`). */
  pulseHomeAvailableFilters: (
    companyID: QueryKey,
    employeeID: QueryKey,
    facetContextKey: string,
    visibilityKey: string
  ) => [
    Configs.platformPostPulseHomeAvailableFiltersAPI,
    companyID,
    employeeID,
    facetContextKey,
    visibilityKey,
  ],

  /** Prefix — invalidate every Pulse Home per-category facet query for a user. */
  pulseHomePerCategoryFacetsPrefix: (companyID: QueryKey, employeeID: QueryKey) => [
    'pulse-home-per-category-facet',
    companyID,
    employeeID,
  ],

  /** Pulse Home — one batch-1 facet category (`platformPostPulseFilters{Category}/`). */
  pulseHomePerCategoryFacet: (
    category: QueryKey,
    companyID: QueryKey,
    employeeID: QueryKey,
    facetContextKey: string,
    visibilityKey: string,
    searchKey = ''
  ) => [
    'pulse-home-per-category-facet',
    companyID,
    employeeID,
    category,
    facetContextKey,
    visibilityKey,
    searchKey,
  ],

  /** Pulse Home — engagement evolution (`platformPostPulseHomeEngagementEvolutionAPI`). */
  pulseHomeEngagementEvolution: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: string,
    visibilityKey: string
  ) => [
    Configs.platformPostPulseHomeEngagementEvolutionAPI,
    companyID,
    employeeID,
    filtersKey,
    visibilityKey,
  ],

  /** Pulse Engagement — scatter plot (`platformGetPulseIndividualEmployeesEngagementAPI`). */
  pulseEngagementReporting: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: string,
    visibilityKey: string
  ) => [
    Configs.platformGetPulseIndividualEmployeesEngagementAPI,
    companyID,
    employeeID,
    filtersKey,
    visibilityKey,
  ],

  /** Pulse Home — last vs previous month (`platformPostPulseHomeEngagementMonthComparisonAPI`). */
  pulseHomeEngagementMonthComparison: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: string,
    visibilityKey: string
  ) => [
    Configs.platformPostPulseHomeEngagementMonthComparisonAPI,
    companyID,
    employeeID,
    filtersKey,
    visibilityKey,
  ],

  /** Pulse Home — one KPI card (`platformPostPulseHomeKeyIndicatorCardAPI`). */
  pulseHomeKeyIndicatorCard: (
    companyID: QueryKey,
    employeeID: QueryKey,
    questionID: QueryKey,
    filtersKey: string,
    visibilityKey: string
  ) => [
    Configs.platformPostPulseHomeKeyIndicatorCardAPI,
    companyID,
    employeeID,
    questionID,
    filtersKey,
    visibilityKey,
  ],

  /** Pulse driver/question profile — paginated questions list (`platformPostPulseProfileQuestionsAPI`). */
  pulseProfileQuestions: (
    companyID: QueryKey,
    employeeID: QueryKey,
    driver: string | null | undefined,
    questionID: QueryKey,
    excludeQuestionID: QueryKey,
    filtersKey: string,
    visibilityKey: string,
    page: number,
    pageSize: number
  ) => [
    Configs.platformPostPulseProfileQuestionsAPI,
    companyID,
    employeeID,
    driver ?? null,
    questionID ?? null,
    excludeQuestionID ?? null,
    filtersKey,
    visibilityKey,
    page,
    pageSize,
  ],

  /** Pulse question/driver profile — lazy heatmap breakdown (`platformPostPulseProfileBreakdownAPI`). */
  pulseProfileBreakdown: (
    companyID: QueryKey,
    employeeID: QueryKey,
    questionID: QueryKey,
    driver: string | null | undefined,
    dimension: string,
    filtersKey: string,
    visibilityKey: string
  ) => [
    Configs.platformPostPulseProfileBreakdownAPI,
    companyID,
    employeeID,
    questionID ?? null,
    driver ?? null,
    dimension,
    filtersKey,
    visibilityKey,
  ],

  /** Campaign Results — lazy heatmap breakdown (`platformPostPulseCampaignProfileBreakdownAPI`). */
  pulseCampaignProfileBreakdown: (
    companyID: QueryKey,
    employeeID: QueryKey,
    campaignID: QueryKey,
    dimension: string,
    filtersKey: string,
    visibilityKey: string
  ) => [
    Configs.platformPostPulseCampaignProfileBreakdownAPI,
    companyID,
    employeeID,
    campaignID,
    dimension,
    filtersKey,
    visibilityKey,
  ],

  /** Pulse question/driver/campaign profile — paginated comments (`platformPostPulseCommentsAPI`). */
  pulseComments: (
    companyID: QueryKey,
    employeeID: QueryKey,
    questionID: QueryKey,
    driver: string | null | undefined,
    campaignID: QueryKey,
    answerSource: string,
    filtersKey: string,
    visibilityKey: string,
    page: number,
    pageSize: number
  ) => [
    Configs.platformPostPulseCommentsAPI,
    companyID,
    employeeID,
    questionID ?? null,
    driver ?? null,
    campaignID ?? null,
    answerSource,
    filtersKey,
    visibilityKey,
    page,
    pageSize,
  ],

  /** Pulse Comments reporting tab — KPIs + paginated feed (`pulseCommentsReportingDataAPI`). */
  pulseCommentsReporting: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: string,
    visibilityKey: string,
    page: number,
    pageSize: number,
    sortBy: string,
    sortDirection: string
  ) => [
    Configs.pulseCommentsReportingDataAPI,
    companyID,
    employeeID,
    filtersKey,
    visibilityKey,
    page,
    pageSize,
    sortBy,
    sortDirection,
  ],

  /** Pulse Questions reporting tab — KPIs + paginated question rows (`pulseQuestionsReportingDataAPI`). */
  pulseQuestionsReporting: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: string,
    visibilityKey: string,
    page: number,
    pageSize: number
  ) => [
    Configs.pulseQuestionsReportingDataAPI,
    companyID,
    employeeID,
    filtersKey,
    visibilityKey,
    page,
    pageSize,
  ],

  /** Pulse Drivers reporting heatmap (`platformGetPulseDriversReportingAPI`). */
  pulseDriversReporting: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: string,
    visibilityKey: string
  ) => [
    Configs.platformGetPulseDriversReportingAPI,
    companyID,
    employeeID,
    filtersKey,
    visibilityKey,
  ],

  /** Pulse Response Rate reporting (`platformGetPulseResponseRateReportingAPI`). */
  pulseResponseRateReporting: (
    companyID: QueryKey,
    employeeID: QueryKey,
    filtersKey: string,
    visibilityKey: string
  ) => [
    Configs.platformGetPulseResponseRateReportingAPI,
    companyID,
    employeeID,
    filtersKey,
    visibilityKey,
  ],


  /** Pulse surveys list grouped by type (`platformGetPulseCampaignsPerCompany`). */
  getPulseCampaignsPerCompany: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetPulseCampaignsPerCompany,
    companyID,
    employeeID,
  ],

  /** Single pulse campaign for edit / read-only summary (`platformGetPulseCampaignIDAPI`). */
  getPulseCampaignById: (companyID: QueryKey, employeeID: QueryKey, campaignID: QueryKey) => [
    Configs.platformGetPulseCampaignIDAPI,
    companyID,
    employeeID,
    campaignID,
  ],

  /** Authoritative Pulse delivery channels for a company. */
  getPulseDeliveryChannels: (companyID: QueryKey) => [
    Configs.platformGetPulseDeliveryChannelsAPI,
    companyID,
  ],

  /** Driver distribution for campaign summary (`platformGetPulseCampaignQuestionDriverDistributionAPI`). */
  getPulseCampaignQuestionDriverDistribution: (
    companyID: QueryKey,
    employeeID: QueryKey,
    campaignID: QueryKey
  ) => [
    Configs.platformGetPulseCampaignQuestionDriverDistributionAPI,
    companyID,
    employeeID,
    campaignID,
  ],

  /** Personal Pulse comparison Saved/History list (`platformGetPulseComparisonsAPI`). */
  getPulseComparisons: (
    companyID: QueryKey,
    employeeID: QueryKey,
    kind: QueryKey,
    limit: QueryKey,
    offset: QueryKey
  ) => [
    Configs.platformGetPulseComparisonsAPI,
    companyID,
    employeeID,
    kind,
    limit,
    offset,
  ],

  /** Prefix: invalidate all pulse comparison library lists for this actor. */
  allPulseComparisonsForActor: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetPulseComparisonsAPI,
    companyID,
    employeeID,
  ],

  /**
   * Latest finalized calibration for `targetEmployeeID`, excluding `excludeCampaignID` (active cycle).
   * GET `platformGetCalibrationLastFinalizedForEmployeeAPI`.
   */
  calibrationLastFinalizedForEmployee: (
    companyID: QueryKey,
    actingEmployeeID: QueryKey,
    excludeCampaignID: QueryKey,
    targetEmployeeID: QueryKey
  ) => [
    Configs.platformGetCalibrationLastFinalizedForEmployeeAPI,
    companyID,
    actingEmployeeID,
    excludeCampaignID,
    targetEmployeeID,
  ],

  /**
   * Prefix: invalidate all last-finalized calibration queries for this actor and excluded campaign
   * (e.g. all team rows on the calibration home list).
   */
  allCalibrationLastFinalizedForActorExcludingCampaign: (
    companyID: QueryKey,
    actingEmployeeID: QueryKey,
    excludeCampaignID: QueryKey
  ) => [
    Configs.platformGetCalibrationLastFinalizedForEmployeeAPI,
    companyID,
    actingEmployeeID,
    excludeCampaignID,
  ],

  /**
   * Prefix: invalidate every last-finalized calibration query for this actor regardless of
   * `excludeCampaignID` (used after writes whose campaign id is not known at invalidation
   * time, e.g. submission flows that close the live campaign).
   */
  allCalibrationLastFinalizedForActor: (companyID: QueryKey, actingEmployeeID: QueryKey) => [
    Configs.platformGetCalibrationLastFinalizedForEmployeeAPI,
    companyID,
    actingEmployeeID,
  ],

  /**
   * One page of the calibration roster (`platformGetCalibrationRosterAPI`).
   * `cursor` is the opaque pagination token; `limit` is the page size (default `100`
   * is encoded as `''` so the first page always matches across observers).
   * `visibility` is `directs`/`org`/`company`, or `'__default__'` when the client omits `visibility`
   * and lets the server apply defaults (query key sentinel only).
   */
  getCalibrationRoster: (
    companyID: QueryKey,
    employeeID: QueryKey,
    cursor: string | null,
    limit: QueryKey,
    bucket: string | null,
    change: string | null,
    visibility: string,
    /** `summary` = KPI aggregates; `scope` = visibility-only people for submit readiness. */
    rosterQueryKind: 'page' | 'summary' | 'scope' = 'page',
    rosterContext: 'home' | 'session' = 'home',
    summaryOnly: boolean = false,
    inclusionKey: string = '',
    exclusionKey: string = '',
    sortKey: string = ''
  ) => [
    Configs.platformGetCalibrationRosterAPI,
    companyID,
    employeeID,
    rosterQueryKind,
    rosterContext,
    summaryOnly ? '1' : '0',
    inclusionKey,
    exclusionKey,
    sortKey,
    cursor ?? '',
    limit,
    bucket ?? '',
    change ?? '',
    visibility,
  ],

  /**
   * Prefix: invalidate every roster page for this actor (any cursor / limit). Used by
   * `useConfirmCalibrationAsManager` and any other mutation that should refresh
   * the calibration list.
   */
  allCalibrationRosterForActor: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetCalibrationRosterAPI,
    companyID,
    employeeID,
  ],

  /** Static organigram facets for Calibration Session filter sidebars. */
  getCalibrationSessionFilterFacets: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetCalibrationSessionFilterFacetsAPI,
    companyID,
    employeeID,
  ],

  /** Elevated-role list of all calibration campaigns (Campaigns tab). */
  getCalibrationCampaignsList: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetCalibrationCampaignsListAPI,
    companyID,
    employeeID,
  ],

  /** One page of the company learning courses catalogue (`platformGetLearningCoursesCatalogueAPI`). */
  getLearningCoursesCatalogue: (
    companyID: QueryKey,
    employeeID: QueryKey,
    cursor: QueryKey,
    limit: QueryKey
  ) => [Configs.platformGetLearningCoursesCatalogueAPI, companyID, employeeID, cursor, limit],

  /**
   * Prefix: invalidate every catalogue page for this actor (any page / limit).
   * Use after course assignment or catalogue mutations.
   */
  allLearningCoursesCatalogueForActor: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetLearningCoursesCatalogueAPI,
    companyID,
    employeeID,
  ],

  /** One page of assigned courses for a target employee (`platformGetLearningEmployeeAssignmentsAPI`). */
  getLearningEmployeeAssignments: (
    companyID: QueryKey,
    employeeID: QueryKey,
    targetEmployeeID: QueryKey,
    page: QueryKey,
    limit: QueryKey,
    statusFilter: QueryKey = ''
  ) => [
    Configs.platformGetLearningEmployeeAssignmentsAPI,
    companyID,
    employeeID,
    targetEmployeeID,
    page,
    limit,
    statusFilter || '',
  ],

  /** Assignment KPI summary for a target employee (`summaryOnly=true`). */
  getLearningEmployeeAssignmentsSummary: (
    companyID: QueryKey,
    employeeID: QueryKey,
    targetEmployeeID: QueryKey
  ) => [
    Configs.platformGetLearningEmployeeAssignmentsAPI,
    companyID,
    employeeID,
    targetEmployeeID,
    'summary',
  ],

  /**
   * Prefix: invalidate every assignments page for this actor + target employee.
   */
  allLearningEmployeeAssignmentsForTarget: (
    companyID: QueryKey,
    employeeID: QueryKey,
    targetEmployeeID: QueryKey
  ) => [Configs.platformGetLearningEmployeeAssignmentsAPI, companyID, employeeID, targetEmployeeID],

  /** News feed page — cursor in the key (Load more fetches the next page). */
  newsFeedPage: (
    companyID: QueryKey,
    employeeID: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    countTotalKey: QueryKey,
    timeframeKey: QueryKey,
    searchKey: QueryKey = ''
  ) => [
    Configs.platformGetNewsFeedAPI,
    companyID,
    employeeID,
    cursor ?? '',
    limit,
    countTotalKey,
    timeframeKey,
    searchKey,
  ],

  allNewsFeedPages: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetNewsFeedAPI,
    companyID,
    employeeID,
  ],

  newsById: (companyID: QueryKey, employeeID: QueryKey, newsID: QueryKey) => [
    Configs.platformGetNewsByIdAPI,
    companyID,
    employeeID,
    newsID,
  ],

  newsCommsListPage: (
    companyID: QueryKey,
    employeeID: QueryKey,
    cursor: QueryKey,
    limit: QueryKey,
    countTotalKey: QueryKey,
    includeInactiveKey: QueryKey,
    timeframeKey: QueryKey
  ) => [
    Configs.platformGetNewsCommsListAPI,
    companyID,
    employeeID,
    cursor ?? '',
    limit,
    countTotalKey,
    includeInactiveKey,
    timeframeKey,
  ],

  allNewsCommsListPages: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetNewsCommsListAPI,
    companyID,
    employeeID,
  ],

  newsComments: (companyID: QueryKey, employeeID: QueryKey, newsID: QueryKey) => [
    Configs.platformGetNewsCommentsAPI,
    companyID,
    employeeID,
    newsID,
  ],

  /** Pending actionable notifications (`platformGetPendingToDosAPI`). */
  getPendingToDos: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetPendingToDosAPI,
    companyID,
    employeeID,
  ],

  /** Key KPI metrics for employee (`platformGetKeyKPIsForEmployeeAPI`). */
  getKeyKPIsForEmployee: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetKeyKPIsForEmployeeAPI,
    companyID,
    employeeID,
  ],

  /** Prefix: invalidate all recruitment job list pages/filters. */
  allRecruitmentJobs: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetRecruitmentJobsAPI,
    companyID,
    employeeID,
  ],

  /** Recruitment jobs list (`platformGetRecruitmentJobsAPI`). */
  getRecruitmentJobs: (
    companyID: QueryKey,
    employeeID: QueryKey,
    status: QueryKey = '',
    page: QueryKey = 1,
    limit: QueryKey = 20
  ) => [
    Configs.platformGetRecruitmentJobsAPI,
    companyID,
    employeeID,
    status ?? '',
    page ?? 1,
    limit ?? 20,
  ],

  /** Prefix: invalidate all recruitment candidate list pages. */
  allRecruitmentCandidates: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetRecruitmentCandidatesAPI,
    companyID,
    employeeID,
  ],

  /** Recruitment talent-pool candidates (`platformGetRecruitmentCandidatesAPI`). */
  getRecruitmentCandidates: (
    companyID: QueryKey,
    employeeID: QueryKey,
    page: QueryKey = 1,
    limit: QueryKey = 20
  ) => [
    Configs.platformGetRecruitmentCandidatesAPI,
    companyID,
    employeeID,
    page ?? 1,
    limit ?? 20,
  ],

  /** Single recruitment job. */
  getRecruitmentJob: (companyID: QueryKey, employeeID: QueryKey, jobID: QueryKey) => [
    Configs.platformGetRecruitmentJobAPI,
    companyID,
    employeeID,
    jobID,
  ],

  /** Prefix: invalidate all application lists for a job. */
  allRecruitmentApplicationsForJob: (
    companyID: QueryKey,
    employeeID: QueryKey,
    jobID: QueryKey
  ) => [Configs.platformGetRecruitmentApplicationsAPI, companyID, employeeID, jobID],

  /** Applications for a job (paginated). */
  getRecruitmentApplications: (
    companyID: QueryKey,
    employeeID: QueryKey,
    jobID: QueryKey,
    stageId: QueryKey = '',
    page: QueryKey = 1,
    limit: QueryKey = 20
  ) => [
    Configs.platformGetRecruitmentApplicationsAPI,
    companyID,
    employeeID,
    jobID,
    stageId ?? '',
    page ?? 1,
    limit ?? 20,
  ],

  /** Public careers page settings for the company. */
  getRecruitmentCareersSettings: (companyID: QueryKey, employeeID: QueryKey) => [
    Configs.platformGetRecruitmentCareersSettingsAPI,
    companyID,
    employeeID,
  ],
};

export const queryKeysInvalidate = createQueryKeysInvalidate(queriesKeys);
